/**
 * Find the first element in a given block whose text matches a placeholder.
 * @param {string} contextSelector
 * @param {string} placeholder
 * @returns {Element|null}
 */
function findPlaceholderEl(contextSelector, placeholder) {
  const elems = document.querySelectorAll(`${contextSelector} *`);
  return Array.from(elems).find(el => el.textContent.trim() === placeholder) || null;
}

/**
 * Insert a minimal Google Map in place of [map].
 * @param {{latitude: string|number, longitude: string|number}} coords
 * @param {string} placeholder
 */
function updateMap({ latitude, longitude }, placeholder) {
  const el = findPlaceholderEl('.retailer_map', placeholder);
  if (!el || !latitude || !longitude) {
    console.log('Missing map placeholder or coords');
    return;
  }

  el.textContent = '';
  Object.assign(el.style, {
    width: '100%',
    height: '700px',
    display: 'block'
  });

  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);

  const map = new google.maps.Map(el, {
    center: { lat, lng },
    zoom: 12,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
    zoomControl: true,
    styles: [
      { stylers: [{ saturation: -100 }, { lightness: 20 }] },
      { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] }
    ]
  });

  new google.maps.Marker({
    position: { lat, lng },
    map
  });
}

/**
 * Replace placeholder text with new text.
 * @param {string} placeholder
 * @param {string} newText
 */
function updateName(placeholder, newText) {
  const el = findPlaceholderEl('.retailer_info', placeholder);
  if (el && newText) {
    el.textContent = newText;
    console.log(`Replaced ${placeholder} with "${newText}"`);
    return;
  }
  console.log(!el ? `No element with placeholder ${placeholder}` : `No data for ${placeholder}`);
}

/**
 * Inject formatted contact HTML into [description].
 * @param {object} data
 * @param {string} placeholder
 */
function updateContactInfo(data, placeholder) {
  const el = findPlaceholderEl('.retailer_info', placeholder);
  if (!el) {
    console.log(`No element with placeholder ${placeholder}`);
    return;
  }

  const { address, city, county, postcode, phone, website, opening_hours } = data;
  let html = '';

  const addr = address ? address.replace(/\n/g, '<br>') : '';
  const parts = [addr, city, county, postcode].filter(p => p);
  if (parts.length) html += `<p>${parts.join('<br>')}</p>`;

  if (phone) {
    const tel = phone.replace(/\s+/g, '');
    html += `<p>Call <a href="tel:${tel}">${phone}</a></p>`;
  }

  if (website) {
    let url = website;
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
    html += `<p><a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a></p>`;
  }

  if (opening_hours) {
    html += `<div class="opening_hours"><h5>Opening Hours</h5>${opening_hours}</div>`;
  }

  el.innerHTML = html;
  console.log('Injected contact info');
}

/**
 * Update the directions button href.
 * @param {object} coords
 */
function updateDirectionsLink({ latitude, longitude }) {
  const btn = document.querySelector('.retailer_info .btn');
  if (!btn) {
    console.log('No .btn in .retailer_info');
    return;
  }
  if (latitude && longitude) {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
    btn.href = url;
    btn.target = '_blank';
    btn.rel = 'noopener noreferrer';
    console.log('Updated directions link');
    return;
  }
  console.log('Missing lat/long');
}

/**
 * Replace image src and remove srcset.
 * @param {string} newSrc
 */
function updateImage(newSrc) {
  const img = document.querySelector('.retailer_image img');
  if (!img) {
    console.log('No image found in .retailer_image');
    return;
  }
  if (newSrc) {
    img.removeAttribute('srcset');
    img.src = newSrc;
    console.log('Updated image src to', newSrc);
    return;
  }
  console.log('No illustration data');
}

/**
 * Insert form HTML in place of [form] placeholder.
 * @param {string} formHtml
 */
function updateForm(formHtml) {
  const el = findPlaceholderEl('.retailer_form', '[form]');
  if (!el) {
    console.log('No element with placeholder [form]');
    return;
  }
  if (formHtml) {
    el.outerHTML = formHtml;
    console.log('Inserted form HTML');
    if (typeof initRecaptchaForms === 'function') {
        initRecaptchaForms();
    }
    return;
  }
  console.log('No form data');
}

/**
 * Updates the bio section with a description.
 * Uses the class .retailer_bio_section
 */
function updateBio(description) {
  // Select by the custom class added in settings
  const sectionEl = document.querySelector('.retailer_bio_section');
  
  if (!sectionEl) {
    console.log('Bio section container (.retailer_bio_section) not found.');
    return;
  }

  if (description && description.trim() !== '') {
    const placeholderP = sectionEl.querySelector('.rich-text__text p');

    if (placeholderP && placeholderP.textContent.trim() === '[bio]') {
      placeholderP.innerHTML = description;

      // Make content section visible (sectionEl is the wrapper div)
      sectionEl.style.display = 'block';

      // Find the divider (sibling in the parent container) and make visible
      if (sectionEl.parentElement) {
        const dividerEl = sectionEl.parentElement.querySelector('.border-top');
        if (dividerEl) {
          dividerEl.style.display = 'block';
        }
      }

      console.log('Updated bio and made section visible.');
    } else {
      console.log('Placeholder <p>[bio]</p> not found or has unexpected content.');
    }
  } else {
    console.log('No description data provided.');
  }
}

/**
 * Updates the image gallery section.
 * Uses the class .retailer_gallery_section
 */
function updateImageGallery(imageData) {
  // Select by the custom class added in settings
  const sectionEl = document.querySelector('.retailer_gallery_section');
  
  if (!sectionEl) {
    console.log('Image gallery section container (.retailer_gallery_section) not found.');
    return;
  }

  const images = [imageData.image_1, imageData.image_2, imageData.image_3].filter(Boolean);

  if (images.length > 0) {
    const placeholderContainers = sectionEl.querySelectorAll('.f-column');

    images.forEach((src, index) => {
      if (placeholderContainers[index]) {
        const motionEl = placeholderContainers[index].querySelector('motion-element');
        if (motionEl) {
          const imgHTML = `<img src="${src}" alt="Retailer Image ${index + 1}" class="w-full h-full object-cover blocks-radius hover-scale-up" loading="lazy">`;
          motionEl.innerHTML = imgHTML;
        }
      }
    });
    
    // Hide unused placeholders
    for (let i = images.length; i < placeholderContainers.length; i++) {
        placeholderContainers[i].style.display = 'none';
    }

    // Make content section visible
    sectionEl.style.display = 'block';

    // Find the divider (sibling in the parent container) and make visible
    if (sectionEl.parentElement) {
      const dividerEl = sectionEl.parentElement.querySelector('.border-top');
      if (dividerEl) {
        dividerEl.style.display = 'block';
      }
    }

    console.log(`Updated gallery with ${images.length} images and made section visible.`);
  } else {
    console.log('No gallery image data provided.');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded');

  const params = new URLSearchParams(window.location.search);
  const id = params.get('id');

  if (!id) {
    console.log('No ID in URL');
    const noId = document.querySelector('#no-id-message-container');
    if (noId) noId.textContent = 'No retailer ID specified.';
    return;
  }

  const api = `https://v4.droplet.weboptichosting.co.uk/retailers/api/get.php?slug=${id}`;
  console.log('Fetching', api);

  fetch(api)
    .then(res => {
      if (!res.ok) throw new Error(res.status);
      return res.json();
    })
    .then(data => {
      console.log('Data', data);

      if (data.level_name) updateName('[subheading]', data.level_name);
      updateName('[heading]', data.name);
      updateName('[retailer_name]', data.name);

      updateContactInfo(
        {
          address: data.address,
          city: data.city,
          county: data.county,
          postcode: data.postcode,
          phone: data.phone,
          website: data.website,
          opening_hours: data.opening_hours
        },
        '[description]'
      );

      updateDirectionsLink({
        latitude: data.latitude,
        longitude: data.longitude
      });

      updateImage(data.illustration);
      updateForm(data.form);
      
      updateMap({
        latitude: data.latitude,
        longitude: data.longitude
      }, '[map]');

      updateBio(data.description);
      updateImageGallery({
        image_1: data.image_1,
        image_2: data.image_2,
        image_3: data.image_3
      });
      
      const spinnerContainer = document.querySelector('.retailer-spinner');
      if (spinnerContainer) {
        spinnerContainer.classList.add('!hidden');
      }
    })
    .catch(err => {
      console.error('Error', err);
      const errEl = document.querySelector('#error-message-container');
      if (errEl) errEl.textContent = 'Sorry, we could not load retailer details.';
    });
});